import { useCallback, useEffect, useRef, useState } from "react";
import type { ImageReference } from "@metalmind-ab/mmagent-chat-ui";

export type ConversationQueueStatus =
  "queued" | "running" | "completed" | "failed" | "cancelled";

export interface ConversationQueueItem {
  submission_id: string;
  status: ConversationQueueStatus;
  position: number | null;
  created_at: string;
  started_at?: string | null;
  finished_at?: string | null;
  job_id?: string | null;
  skill_name?: string | null;
  reason?: string | null;
}

interface QueueSnapshot {
  conversation_id: string | number;
  items: ConversationQueueItem[];
}

interface QueueSseFrame {
  type?: string;
  conversation_id?: string | number;
  submission_id?: string;
  position?: number;
  status?: string;
  reason?: string;
  job_id?: string;
  skill_name?: string;
}

interface UseConversationQueueOptions {
  apiBaseUrl: string;
  workspaceId: string;
  conversationId: string | number | null;
  getAuthHeaders: () => Promise<Record<string, string>>;
}

const VISIBLE_STATUSES = new Set<ConversationQueueStatus>([
  "queued",
  "running",
  "failed",
]);

function queueItemsFromSnapshot(value: unknown): ConversationQueueItem[] {
  if (!value || typeof value !== "object") {
    throw new Error("Invalid queue response");
  }
  const items = (value as QueueSnapshot).items;
  if (!Array.isArray(items)) throw new Error("Invalid queue response");
  return items.filter(
    (item): item is ConversationQueueItem =>
      Boolean(item) &&
      typeof item.submission_id === "string" &&
      (typeof item.position === "number" || item.position === null) &&
      typeof item.created_at === "string" &&
      VISIBLE_STATUSES.has(item.status),
  );
}

function compareQueuePosition(
  left: ConversationQueueItem,
  right: ConversationQueueItem,
): number {
  return (
    (left.position ?? Number.MAX_SAFE_INTEGER) -
    (right.position ?? Number.MAX_SAFE_INTEGER)
  );
}

function mergeQueueItem(
  items: ConversationQueueItem[],
  next: ConversationQueueItem,
): ConversationQueueItem[] {
  return [
    ...items.filter((item) => item.submission_id !== next.submission_id),
    next,
  ]
    .filter((item) => VISIBLE_STATUSES.has(item.status))
    .sort(compareQueuePosition);
}

async function readQueueSubmissionStream(
  response: Response,
  onFrame: (frame: QueueSseFrame) => void,
): Promise<void> {
  if (!response.body) throw new Error("Queue response stream unavailable");
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split(/\r?\n/);
      buffer = lines.pop() ?? "";
      for (const line of lines) {
        if (!line.startsWith("data:")) continue;
        try {
          const frame = JSON.parse(line.slice(5).trim()) as QueueSseFrame;
          onFrame(frame);
        } catch {
          // Ignore non-JSON keepalive or extension frames.
        }
      }
    }
  } finally {
    reader.releaseLock();
  }
}

export function useConversationQueue({
  apiBaseUrl,
  workspaceId,
  conversationId,
  getAuthHeaders,
}: UseConversationQueueOptions) {
  const [items, setItems] = useState<ConversationQueueItem[]>([]);
  const [error, setError] = useState<string | null>(null);
  const requestGeneration = useRef(0);
  const refreshInFlight = useRef<{
    key: string;
    promise: Promise<void>;
  } | null>(null);
  const selectedConversationRef = useRef(conversationId);
  selectedConversationRef.current = conversationId;
  const conversationPath = conversationId
    ? `${apiBaseUrl}/chat/conversations/${encodeURIComponent(String(conversationId))}`
    : null;

  const refresh = useCallback(() => {
    if (!conversationPath) {
      setItems([]);
      setError(null);
      return Promise.resolve();
    }
    const generation = requestGeneration.current;
    const key = `${generation}:${conversationPath}`;
    if (refreshInFlight.current?.key === key) {
      return refreshInFlight.current.promise;
    }

    const promise = Promise.resolve().then(async () => {
      try {
        const headers = await getAuthHeaders();
        const response = await fetch(`${conversationPath}/queue`, {
          headers: { ...headers, "X-Workspace-Id": workspaceId },
        });
        if (!response.ok) throw new Error("Queue status request failed");
        const nextItems = queueItemsFromSnapshot(await response.json());
        if (generation !== requestGeneration.current) return;
        setItems(nextItems.sort(compareQueuePosition));
        setError(null);
      } catch {
        if (generation !== requestGeneration.current) return;
        setError("Couldn't load queued messages.");
      }
    });
    const inFlight = { key, promise };
    refreshInFlight.current = inFlight;
    void promise.then(() => {
      if (refreshInFlight.current === inFlight) refreshInFlight.current = null;
    });
    return promise;
  }, [conversationPath, getAuthHeaders, workspaceId]);

  const handleFrame = useCallback(
    (frame: QueueSseFrame) => {
      if (
        !selectedConversationRef.current ||
        String(frame.conversation_id) !==
          String(selectedConversationRef.current)
      ) {
        return;
      }
      if (frame.type === "queued" && frame.submission_id) {
        setItems((current) =>
          mergeQueueItem(current, {
            submission_id: frame.submission_id!,
            status: "queued",
            position: frame.position ?? 1,
            created_at: new Date().toISOString(),
          }),
        );
      } else if (
        frame.type === "assistant_turn_started" &&
        frame.submission_id
      ) {
        setItems((current) =>
          current.map((item) =>
            item.submission_id === frame.submission_id
              ? { ...item, status: "running", position: 0 }
              : item,
          ),
        );
        void refresh();
      } else if (frame.type === "queue_item_failed" && frame.submission_id) {
        setItems((current) =>
          mergeQueueItem(current, {
            submission_id: frame.submission_id!,
            status: "failed",
            reason: frame.reason ?? "worker_restarted",
            position: frame.position ?? 0,
            created_at: new Date().toISOString(),
          }),
        );
      } else if (frame.type === "job_started" && frame.submission_id) {
        setItems((current) =>
          current.map((item) =>
            item.submission_id === frame.submission_id
              ? {
                  ...item,
                  status: "running",
                  ...(frame.job_id ? { job_id: frame.job_id } : {}),
                  ...(frame.skill_name ? { skill_name: frame.skill_name } : {}),
                }
              : item,
          ),
        );
        void refresh();
      } else if (
        frame.type === "job_started" ||
        frame.type === "job_progress" ||
        frame.type === "job_completed" ||
        frame.type === "job_failed" ||
        frame.type === "done" ||
        frame.type === "cancelled" ||
        frame.type === "error"
      ) {
        void refresh();
      }
    },
    [refresh],
  );

  useEffect(() => {
    requestGeneration.current += 1;
    setItems([]);
    setError(null);
    if (conversationPath) void refresh();
  }, [conversationId, conversationPath, refresh]);

  useEffect(() => {
    const handleOnline = () => {
      void refresh();
    };
    window.addEventListener("online", handleOnline);
    return () => window.removeEventListener("online", handleOnline);
  }, [refresh]);

  const enqueue = useCallback(
    async (content: string, images?: ImageReference[]) => {
      if (!conversationPath) throw new Error("Select a conversation first");
      let response: Response;
      try {
        const headers = await getAuthHeaders();
        const payload: { content: string; images?: ImageReference[] } = {
          content,
        };
        if (images?.length) payload.images = images;
        response = await fetch(`${conversationPath}/messages/stream`, {
          method: "POST",
          headers: {
            ...headers,
            "X-Workspace-Id": workspaceId,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        });
      } catch {
        setError("Couldn't queue this message.");
        throw new Error("Queue submission failed");
      }
      if (!response.ok) {
        setError("Couldn't queue this message.");
        throw new Error("Queue submission failed");
      }
      setError(null);
      let acknowledged = false;
      let resolveAcknowledgement!: () => void;
      let rejectAcknowledgement!: (error: Error) => void;
      const acknowledgement = new Promise<void>((resolve, reject) => {
        resolveAcknowledgement = resolve;
        rejectAcknowledgement = reject;
      });
      const acknowledgementTimeout = window.setTimeout(() => {
        if (!acknowledged) {
          rejectAcknowledgement(new Error("Queue acknowledgement timed out"));
        }
      }, 10_000);
      const stream = readQueueSubmissionStream(response, (frame) => {
        handleFrame(frame);
        if (
          !acknowledged &&
          (frame.type === "queued" || frame.type === "assistant_turn_started")
        ) {
          acknowledged = true;
          window.clearTimeout(acknowledgementTimeout);
          resolveAcknowledgement();
        }
      })
        .then(async () => {
          if (!acknowledged) {
            throw new Error("Queue stream ended before acknowledgement");
          }
          await refresh();
        })
        .catch((error: unknown) => {
          setError("Couldn't queue this message.");
          if (!acknowledged) {
            window.clearTimeout(acknowledgementTimeout);
            rejectAcknowledgement(
              error instanceof Error
                ? error
                : new Error("Queue submission failed"),
            );
          }
        });
      // Keep consuming the request's shared-turn stream, but do not make the
      // composer wait for the queued assistant response before clearing input.
      void stream;
      // The queued frame acknowledges admission. The response stream continues
      // to update shared progress and is drained in the background.
      await acknowledgement;
    },
    [conversationPath, getAuthHeaders, handleFrame, refresh, workspaceId],
  );

  const cancelItem = useCallback(
    async (item: ConversationQueueItem) => {
      if (!conversationPath) return;
      try {
        const headers = await getAuthHeaders();
        const endpoint = item.job_id
          ? `${apiBaseUrl}/chat/jobs/${encodeURIComponent(item.job_id)}/cancel`
          : `${conversationPath}/queue/${encodeURIComponent(item.submission_id)}/cancel`;
        const response = await fetch(endpoint, {
          method: "POST",
          headers: {
            ...headers,
            "X-Workspace-Id": workspaceId,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(
            item.job_id ? { submission_id: item.submission_id } : {},
          ),
        });
        if (!response.ok) throw new Error("Queue cancellation failed");
        await refresh();
      } catch {
        setError("Couldn't cancel this queued message.");
      }
    },
    [apiBaseUrl, conversationPath, getAuthHeaders, refresh, workspaceId],
  );

  const cancelAll = useCallback(async () => {
    if (!conversationPath) return;
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${conversationPath}/queue/cancel-all`, {
        method: "POST",
        headers: { ...headers, "X-Workspace-Id": workspaceId },
      });
      if (!response.ok) throw new Error("Queue cancellation failed");
      await refresh();
    } catch {
      setError("Couldn't cancel queued messages.");
    }
  }, [conversationPath, getAuthHeaders, refresh, workspaceId]);

  return { items, error, refresh, enqueue, cancelItem, cancelAll, handleFrame };
}
